// src/pages/DebtStudio/components/Issues/DealStructure/hooks/ldsSolver.js

import numeric from "numeric";
import dayjs from "lib/dayjs";
import { dayCountFraction } from "lib/dayCount";
import { trunc3 } from "pages/DebtStudio/utils/formatters";
import { computeCapI } from "pages/DebtStudio/lib/computeCapI";
import { optimizeDenominations } from "./optimizeDenominations";

/** 🔧 helpers for canonical rounding */
const roundPrice3 = (p) => Math.round(Number(p) * 1000) / 1000;
const roundCents = (x) => Math.round(Number(x) * 100) / 100;
const ceilCents = (x) => Math.ceil((Number(x) || 0) * 100 - 1e-9) / 100;

function normalizeFinancingType(value) {
  return String(value || "bond").trim().toLowerCase();
}

function isLoanFinancing(financingType) {
  return normalizeFinancingType(financingType) === "loan";
}

function shouldUseCommercialLoanPaymentRounding({ financingType }) {
  return isLoanFinancing(financingType);
}

function shouldBypassDenominationOptimizer({ financingType }) {
  return isLoanFinancing(financingType);
}

function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

function normalizeDenom(val) {
  const n = Number(val);
  if (!Number.isFinite(n) || n < 0.01) return 0.01;
  return n;
}

const dj = (d) => (d ? dayjs(d) : null);

function principalFreqToStepMonthsFromValue(principalFrequency) {
  const freqToMonths = {
    monthly: 1,
    bimonthly: 2,
    quarterly: 3,
    every_four_months: 4,
    triannual: 4,
    semiannual: 6,
    annual: 12,
  };

  return freqToMonths[String(principalFrequency || "annual").toLowerCase()] ?? 12;
}

function generatePrincipalDateSetFromParams({
  firstPrincipalDate,
  finalMaturityDate,
  principalFrequency = "annual",
}) {
  const fp = dj(firstPrincipalDate);
  const fm = dj(finalMaturityDate);
  if (!fp || !fm || !fp.isValid() || !fm.isValid()) return new Set();

  const stepMonths = principalFreqToStepMonthsFromValue(principalFrequency);
  const s = new Set();

  let cur = fp.startOf("day");
  for (let guard = 0; guard < 5000; guard++) {
    if (cur.isAfter(fm)) break;
    s.add(cur.format("YYYY-MM-DD"));
    cur = cur.add(stepMonths, "month");
  }

  s.add(fm.format("YYYY-MM-DD"));
  return s;
}

export function deriveLdsPrincipalEndDates({
  rows = [],
  firstPrincipalDate,
  finalMaturityDate,
  principalFrequency = "annual",
}) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const expanded = [...rows].sort((a, b) => String(a.date).localeCompare(String(b.date)));

  const principalDateSet = generatePrincipalDateSetFromParams({
    firstPrincipalDate,
    finalMaturityDate,
    principalFrequency,
  });

  const out = [];

  for (const r of expanded) {
    const d = dayjs(r?.date);
    if (!d.isValid()) continue;

    const iso = d.format("YYYY-MM-DD");
    if (principalDateSet.has(iso)) out.push(iso);
  }

  if (!out.length) {
    const fallback = dayjs(expanded[expanded.length - 1]?.date);
    if (fallback.isValid()) out.push(fallback.format("YYYY-MM-DD"));
  }

  return out;
}

function buildBaseDebtServiceBySegment({
  baseDebtServiceByPeriod = [],
  wrapBaseByPeriod = [],
  segments = [],
  expanded = [],
}) {
  const source =
    Array.isArray(baseDebtServiceByPeriod) && baseDebtServiceByPeriod.length
      ? baseDebtServiceByPeriod
      : Array.isArray(wrapBaseByPeriod)
        ? wrapBaseByPeriod
        : [];

  const baseMap = new Map(
    source
      .filter((x) => x && typeof x === "object")
      .map((x) => [String(x.date || ""), num(x.total ?? x.amount ?? x.value, 0)])
      .filter(([d]) => !!d)
  );

  return segments.map((seg) => {
    const d = expanded?.[seg.end]?.date;
    return d && baseMap.has(String(d)) ? num(baseMap.get(String(d)), 0) : 0;
  });
}

export function solveLDSStructure(params = {}) {
  const {
    rows = [],
    targetType = "par_amount",
    targetValue,
    financingType = "bond",
    denomination,
    couponFrequency = "semiannual",
    principalFrequency = "annual",
    dayCount = "30/360",
    datedDate,
    deliveryDate,
    firstCouponDate,
    firstPrincipalDate,
    finalMaturityDate,
    costOfIssuance = 0,
    additionalSources = 0,
    dealType,
    capiEnabled = false,
    capiDate = "",
    capiInvestmentRate = 0,
    amortizationPeriods,
    baseDebtServiceByPeriod = [],
    wrapBaseByPeriod = [],
  } = params;

  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      schedule: [],
      parAmount: 0,
      grossProceeds: 0,
      netProceeds: 0,
      coi: 0,
      capiUse: 0,
    };
  }

  function principalFreqToStepMonths() {
    return principalFreqToStepMonthsFromValue(principalFrequency);
  }

  function generatePrincipalDateSet() {
    return generatePrincipalDateSetFromParams({
      firstPrincipalDate,
      finalMaturityDate,
      principalFrequency,
    });
  }

  const pctOrDec = (v) => {
    if (v == null || v === "") return null;
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    return n > 1 ? n / 100 : n;
  };

  function couponFreqToStepMonths(couponFrequency) {
    const freq = String(couponFrequency || "semiannual").toLowerCase();
    const map = {
      monthly: 1,
      bimonthly: 2,
      quarterly: 3,
      every_four_months: 4,
      triannual: 4,
      semiannual: 6,
      annual: 12,
    };
    return map[freq] ?? 6;
  }

  function computeStubFactor({
    startDate,
    endDate,
    dayCount,
    principalStepMonths,
  }) {
    const dc = dayCount || "30/360";

    const start = startDate ? dayjs(startDate).startOf("day") : null;
    const end = endDate ? dayjs(endDate).startOf("day") : null;

    if (!start || !end || !start.isValid() || !end.isValid()) return 1;
    if (!end.isAfter(start, "day")) return 1;

    const stepMonths = Number(principalStepMonths) || 12;
    const expectedStart = end.clone().subtract(stepMonths, "month");

    const actualFrac = dayCountFraction(start.toDate(), end.toDate(), dc);
    const expectedFrac = dayCountFraction(
      expectedStart.toDate(),
      end.toDate(),
      dc
    );

    if (!Number.isFinite(actualFrac) || !Number.isFinite(expectedFrac)) return 1;
    if (expectedFrac <= 0) return 1;

    const factor = actualFrac / expectedFrac;

    return Number.isFinite(factor) && factor > 0 && factor < 1 ? factor : 1;
  }

  function computeSegmentTargetFactors({ segments = [], expanded = [], dayCount }) {
    const startDate = deliveryDate || datedDate;

    return segments.map((seg, y) => {
      if (y !== 0) return 1;

      return computeStubFactor({
        startDate,
        endDate: expanded?.[seg.end]?.date,
        dayCount,
        principalStepMonths: principalFreqToStepMonths(),
      });
    });
  }

  function computeSegmentTargetAdjustments({ segments = [] }) {
    return Array(segments.length).fill(0);
  }

  function computeAccruedInterestFactor({ couponAnnual, dayCount }) {
    const dc = dayCount || "30/360";
    const dd = datedDate ? dayjs(datedDate).startOf("day") : null;
    const dlv = deliveryDate ? dayjs(deliveryDate).startOf("day") : null;

    if (!dd || !dlv || !dd.isValid() || !dlv.isValid()) return 0;
    if (!dlv.isAfter(dd, "day")) return 0;

    const frac = dayCountFraction(dd.toDate(), dlv.toDate(), dc);
    if (!Number.isFinite(frac) || frac <= 0) return 0;

    return (Number(couponAnnual) || 0) * frac;
  }

  function computeSegmentAccruedInterestFactors({
    segments = [],
    couponAnnual = [],
    dayCount,
  }) {
    return segments.map((seg) =>
      computeAccruedInterestFactor({
        couponAnnual: couponAnnual?.[seg.end] || 0,
        dayCount,
      })
    );
  }

  function computeSegmentAccrualFactors({
    datedDate,
    firstCouponDate,
    finalMaturityDate,
    couponFrequency,
    dayCount,
    principalEndDates,
    principalStepMonths,
  }) {
    const dc = dayCount || "30/360";
    const dd = datedDate ? dayjs(datedDate).startOf("day") : null;
    const fc = firstCouponDate ? dayjs(firstCouponDate).startOf("day") : null;
    const fm = finalMaturityDate ? dayjs(finalMaturityDate).startOf("day") : null;

    if (
      !dd ||
      !fc ||
      !fm ||
      !Array.isArray(principalEndDates) ||
      !principalEndDates.length
    ) {
      return Array(principalEndDates?.length || 0).fill(0);
    }

    const pEnds = principalEndDates
      .map((d) => dayjs(d).startOf("day"))
      .filter((d) => d && d.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf());

    if (!pEnds.length) return [];

    const couponStepMonths = couponFreqToStepMonths(couponFrequency);
    const couponDates = [];
    let cur = fc.clone();

    for (let guard = 0; guard < 5000; guard++) {
      if (cur.isAfter(fm)) break;
      couponDates.push(cur.clone());
      cur = cur.add(couponStepMonths, "month");
    }

    const fmIso = fm.format("YYYY-MM-DD");
    if (!couponDates.some((d) => d.format("YYYY-MM-DD") === fmIso)) {
      if (!fm.isBefore(fc) && !fm.isBefore(dd)) couponDates.push(fm.clone());
      couponDates.sort((a, b) => a.valueOf() - b.valueOf());
    }

    const startEx = [];
    for (let y = 0; y < pEnds.length; y++) {
      if (y === 0) {
        startEx.push(pEnds[0].clone().subtract(principalStepMonths, "month"));
      } else {
        startEx.push(pEnds[y - 1].clone());
      }
    }

    const accrualFactors = Array(pEnds.length).fill(0);
    let prevCoupon = dd.clone();
    let yPtr = 0;

    for (let i = 0; i < couponDates.length; i++) {
      const payDate = couponDates[i];

      if (!payDate.isAfter(dd)) {
        prevCoupon = payDate.clone();
        continue;
      }

      while (yPtr < pEnds.length && payDate.isAfter(pEnds[yPtr])) yPtr++;
      if (yPtr >= pEnds.length) break;

      const start = startEx[yPtr];
      const end = pEnds[yPtr];

      if (!(payDate.isAfter(start) && (payDate.isSame(end) || payDate.isBefore(end)))) {
        prevCoupon = payDate.clone();
        continue;
      }

      const frac = dayCountFraction(prevCoupon.toDate(), payDate.toDate(), dc);
      accrualFactors[yPtr] += Number(frac) || 0;

      prevCoupon = payDate.clone();
    }

    return accrualFactors;
  }

  function computeCapIUse(scheduleForMath, prebuiltDebtServiceSchedule = null) {
    if (!capiEnabled) return 0;

    try {
      const capi = computeCapI({
        dealType,
        capiEnabled,
        capiDate,
        capiInvestmentRate,
        rows: scheduleForMath,
        form: {
          datedDate,
          deliveryDate,
          firstCouponDate,
          finalMaturityDate,
          couponFrequency,
          dayCount,
        },
        prebuiltDebtServiceSchedule,
      });

      const capiUse = Number(capi?.capiUse ?? capi?.use ?? 0);
      return Number.isFinite(capiUse) ? roundCents(capiUse) : 0;
    } catch (e) {
      console.warn("[LDS] computeCapI failed, defaulting to 0", e);
      return 0;
    }
  }

  function runDenominationOptimizer({
    result,
    optimizerTargetType,
    proceedsTarget = null,
  }) {
    const optimized = optimizeDenominations({
      result,
      denomination,
      targetType: optimizerTargetType,
      proceedsTarget,
      form: {
        datedDate,
        deliveryDate,
        firstCouponDate,
        finalMaturityDate,
        couponFrequency,
        dayCount,
      },
      costOfIssuance,
      computeCapIUse,
      roundPrice3,
      roundCents,
    });

    // eslint-disable-next-line no-unused-vars
    const { scheduleForMath, ...out } = optimized;
    return out;
  }

  function buildExpandedRows() {
    const termGroups = {};
    for (const r of rows) {
      if (r?.bond_type === "term" && r?.term_group) {
        if (!termGroups[r.term_group]) termGroups[r.term_group] = [];
        termGroups[r.term_group].push(r);
      }
    }

    const sharedTermData = new Map();
    Object.values(termGroups).forEach((members) => {
      if (!members.length) return;

      members.sort((a, b) => String(a.date).localeCompare(String(b.date)));
      const last = members[members.length - 1];

      const cDec = pctOrDec(last.coupon);
      const anchorRaw = Number.isFinite(last?.price) ? Number(last.price) : 100;
      const anchorTrunc = trunc3(anchorRaw);
      const anchorPrice = anchorTrunc == null ? 100 : anchorTrunc;

      sharedTermData.set(last.term_group, {
        couponDec: cDec,
        price: anchorPrice,
      });
    });

    const expanded = rows.map((r) => {
      if (
        r?.bond_type === "term" &&
        r?.term_group &&
        sharedTermData.has(r.term_group)
      ) {
        const { couponDec, price } = sharedTermData.get(r.term_group);
        return {
          ...r,
          coupon: couponDec != null && couponDec <= 1 ? couponDec * 100 : r.coupon,
          price: trunc3(price ?? 100) ?? 100,
        };
      }

      const p = Number.isFinite(r?.price) ? r.price : 100;
      return { ...r, price: roundPrice3(p) };
    });

    expanded.sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return expanded;
  }

  function buildSegments(expanded) {
    const n = expanded.length;
    const principalDateSet = generatePrincipalDateSet();
    const principalIdx = [];

    for (let i = 0; i < n; i++) {
      const d = dayjs(expanded[i].date).format("YYYY-MM-DD");
      if (principalDateSet.has(d)) principalIdx.push(i);
    }

    if (principalIdx.length === 0) {
      principalIdx.push(n - 1);
    }

    let start0 = 0;
    try {
      const fp = dj(firstPrincipalDate);
      if (fp && principalIdx.length > 0) {
        const stepMonths = principalFreqToStepMonths();
        const priorBoundary = fp.startOf("day").subtract(stepMonths, "month");

        let lastAtOrBefore = -1;
        for (let i = 0; i <= principalIdx[0]; i++) {
          const di = dayjs(expanded[i].date).startOf("day");
          if (di.isSame(priorBoundary) || di.isBefore(priorBoundary)) {
            lastAtOrBefore = i;
          }
        }

        start0 = Math.min(principalIdx[0], Math.max(0, lastAtOrBefore + 1));
      }
    } catch (e) {
      start0 = 0;
    }

    const segments = [];
    for (let y = 0; y < principalIdx.length; y++) {
      const end = principalIdx[y];
      const start = y === 0 ? start0 : principalIdx[y - 1] + 1;
      segments.push({ start, end });
    }

    return { principalIdx, start0, segments };
  }

  function collapseTermGroupsForPresentation(scheduleForMath) {
    const lastByGroup = {};
    for (const r of scheduleForMath) {
      if (r?.bond_type === "term" && r?.term_group) {
        const g = r.term_group;
        if (!lastByGroup[g] || (r.date && r.date > lastByGroup[g].date)) {
          lastByGroup[g] = r;
        }
      }
    }

    return scheduleForMath.map((r) => {
      if (r?.bond_type === "term" && r?.term_group) {
        const isLast = lastByGroup[r.term_group] === r;
        return isLast ? { ...r } : { ...r, yield: null, coupon: null };
      }
      return { ...r };
    });
  }

  function solveWithActiveSet(Afull, bfull, parAmount, numPeriods) {
    const rawSol = numeric.solve(Afull, bfull);
    const rawPrincipals = rawSol.slice(0, numPeriods);
    const rawLevelPayment = rawSol[numPeriods];

    const fixedZero = new Set();

    for (let iter = 0; iter < numPeriods; iter++) {
      const freeIdx = [];
      for (let i = 0; i < numPeriods; i++) {
        if (!fixedZero.has(i)) freeIdx.push(i);
      }

      const m = freeIdx.length + 1;
      const Ared = numeric.rep([m, m], 0);
      const bred = Array(m).fill(0);

      for (let r = 0; r < freeIdx.length; r++) {
        const oldRow = freeIdx[r];

        for (let c = 0; c < freeIdx.length; c++) {
          const oldCol = freeIdx[c];
          Ared[r][c] = Afull[oldRow][oldCol];
        }

        Ared[r][freeIdx.length] = Afull[oldRow][numPeriods];
        bred[r] = bfull[oldRow];
      }

      for (let c = 0; c < freeIdx.length; c++) {
        Ared[freeIdx.length][c] = 1;
      }

      bred[freeIdx.length] = parAmount;

      const sol = numeric.solve(Ared, bred);

      const principals = Array(numPeriods).fill(0);
      for (let i = 0; i < freeIdx.length; i++) {
        principals[freeIdx[i]] = sol[i];
      }

      const negative = principals
        .map((x, i) => ({ x, i }))
        .filter(({ x }) => Number.isFinite(x) && x < -1e-6)
        .sort((a, b) => a.x - b.x)[0];

      if (!negative) {
        return {
          principalsByPeriod: principals.map((x) =>
            Number.isFinite(x) && x > 0 ? x : 0
          ),
          levelPayment: sol[freeIdx.length],
          fixedZero: Array.from(fixedZero),
          iterations: iter + 1,
          rawPrincipals,
          rawLevelPayment,
        };
      }

      fixedZero.add(negative.i);
    }

    return {
      principalsByPeriod: rawPrincipals.map((x) =>
        Number.isFinite(x) && x > 0 ? x : 0
      ),
      levelPayment: rawLevelPayment,
      fixedZero: Array.from(fixedZero),
      iterations: numPeriods,
      rawPrincipals,
      rawLevelPayment,
      fallback: true,
    };
  }

  function solveForParAmount(parAmount) {
    if (!(parAmount > 0)) throw new Error("parAmount must be > 0");

    const denom = normalizeDenom(denomination);
    const dc = dayCount || "30/360";

    const expanded = buildExpandedRows();

    const couponAnnual = expanded.map((m) => {
      let x = Number(m?.coupon ?? 0);
      if (!Number.isFinite(x)) x = 0;
      if (x > 1) x = x / 100;
      if (x < 0) x = 0;
      return x;
    });

    const { segments } = buildSegments(expanded);
    const numPeriods = segments.length;
    const termPeriods = numPeriods;

    const loanAmortizationPeriods = Math.max(
      Number(amortizationPeriods || termPeriods),
      termPeriods
    );

    const principalEndDates = segments
      .map((seg) => expanded?.[seg.end]?.date)
      .filter(Boolean);

    const segAccrualFactor = computeSegmentAccrualFactors({
      datedDate,
      firstCouponDate,
      finalMaturityDate,
      couponFrequency,
      dayCount: dc,
      principalEndDates,
      principalStepMonths: principalFreqToStepMonths(),
    });

    const segmentCouponAnnual = segments.map(
      (seg) => couponAnnual?.[seg.end] || 0
    );
    const segInterestFactor = segmentCouponAnnual.map(
      (coupon, y) => coupon * (segAccrualFactor?.[y] || 0)
    );

    const segmentTargetFactors = computeSegmentTargetFactors({
      segments,
      expanded,
      dayCount: dc,
    });

    const segmentTargetAdjustments = computeSegmentTargetAdjustments({
      segments,
    });

    const segmentAccruedInterestFactors = computeSegmentAccruedInterestFactors({
      segments,
      couponAnnual,
      dayCount: dc,
    });

    const baseTotals = buildBaseDebtServiceBySegment({
      baseDebtServiceByPeriod,
      wrapBaseByPeriod,
      segments,
      expanded,
    });

    const scheduleForMath = expanded.map((m) => ({ ...m, principal: 0 }));

    if (shouldUseCommercialLoanPaymentRounding({ financingType })) {

      const amortFactors = Array.from(
        { length: loanAmortizationPeriods },
        (_, i) => segInterestFactor[i % termPeriods] || 0
      );

      let discount = 1;
      let annuityFactor = 0;

      for (let i = 0; i < loanAmortizationPeriods; i++) {
        discount /= 1 + amortFactors[i];
        annuityFactor += discount;
      }

      const paymentRaw =
        annuityFactor > 0
          ? parAmount / annuityFactor
          : parAmount / loanAmortizationPeriods;

      const levelPayment = roundCents(paymentRaw);
      const roundedLoanPayment = ceilCents(paymentRaw);

      let remainingBalance = roundCents(parAmount);
      let balloonAmount = 0;

      for (let y = 0; y < termPeriods; y++) {
        const principalRowIdx = segments[y].end;
        const isFinalPeriod = y === termPeriods - 1;

        const interestY = roundCents(
          remainingBalance * (segInterestFactor[y] || 0)
        );

        let principalY;

        if (isFinalPeriod) {
          balloonAmount =
            loanAmortizationPeriods > termPeriods ? remainingBalance : 0;

          principalY = remainingBalance;
        } else {
          principalY = roundCents(roundedLoanPayment - interestY);
          if (principalY < 0) principalY = 0;
          principalY = Math.min(principalY, remainingBalance);
        }

        scheduleForMath[principalRowIdx].principal = roundCents(principalY);
        remainingBalance = roundCents(remainingBalance - principalY);

      }

      const roundedPar = scheduleForMath.reduce(
        (s, r) => s + (r.principal || 0),
        0
      );

      const grossProceeds = scheduleForMath.reduce((acc, r) => {
        const p3 = roundPrice3(r.price ?? 100);
        const row = roundCents((r.principal || 0) * (p3 / 100));
        return acc + row;
      }, 0);

      const coi =
        typeof costOfIssuance === "function"
          ? costOfIssuance(roundedPar)
          : Number(costOfIssuance) || 0;

      const capiUse = computeCapIUse(scheduleForMath);
      const netProceeds = roundCents(grossProceeds - coi - capiUse);

      const schedule = collapseTermGroupsForPresentation(scheduleForMath);

      return {
        schedule,
        scheduleForMath,
        levelPayment,
        scheduledPayment: roundedLoanPayment,
        amortizationPeriods: loanAmortizationPeriods,
        loanTermPeriods: termPeriods,
        balloonAmount: roundCents(balloonAmount),
        parAmount: roundedPar,
        grossProceeds,
        netProceeds,
        coi,
        capiUse,
      };
    }

    const A = numeric.rep([numPeriods + 1, numPeriods + 1], 0);
    const b = Array(numPeriods + 1).fill(0);

    for (let y = 0; y < numPeriods; y++) {
      for (let j = y; j < numPeriods; j++) {
        A[y][j] +=
          (segAccrualFactor[y] || 0) * (segmentCouponAnnual[j] || 0);

        if (y === 0) {
          A[y][j] -= segmentAccruedInterestFactors[j] || 0;
        }
      }

      A[y][y] += 1;
      A[y][numPeriods] = -(segmentTargetFactors[y] || 1);

      b[y] = Number(segmentTargetAdjustments[y] || 0) - num(baseTotals[y], 0);
    }

    for (let j = 0; j < numPeriods; j++) A[numPeriods][j] = 1;
    b[numPeriods] = parAmount;

    const active = solveWithActiveSet(A, b, parAmount, numPeriods);

    const principalsByPeriod = active.principalsByPeriod;
    const levelPayment = roundCents(active.levelPayment);

    const precision = Math.max(0, -Math.log10(denom));
    const units = principalsByPeriod.map((p) => p / denom);
    const floors = units.map((u) => Math.floor(u));
    const remainders = units.map((u, idx) => ({ idx, rem: u - floors[idx] }));

    const allocated = floors.reduce((s, x) => s + x, 0);
    const desiredUnits = Math.round(parAmount / denom);
    const needed = desiredUnits - allocated;

    remainders.sort((a, b) => b.rem - a.rem);

    for (let k = 0; k < needed; k++) {
      if (remainders[k]) floors[remainders[k].idx] += 1;
    }

    const roundedPeriodPrincipals = floors.map((u) =>
      Number((u * denom).toFixed(precision))
    );

    for (let y = 0; y < numPeriods; y++) {
      const principalRowIdx = segments[y].end;
      scheduleForMath[principalRowIdx].principal = Math.max(
        0,
        roundedPeriodPrincipals[y]
      );
    }

    const roundedPar = scheduleForMath.reduce(
      (s, r) => s + (r.principal || 0),
      0
    );

    const grossProceeds = scheduleForMath.reduce((acc, r) => {
      const p3 = roundPrice3(r.price ?? 100);
      const row = roundCents((r.principal || 0) * (p3 / 100));
      return acc + row;
    }, 0);

    const coi =
      typeof costOfIssuance === "function"
        ? costOfIssuance(roundedPar)
        : Number(costOfIssuance) || 0;

    const capiUse = computeCapIUse(scheduleForMath);
    const netProceeds = roundCents(grossProceeds - coi - capiUse);

    const schedule = collapseTermGroupsForPresentation(scheduleForMath);

    return {
      schedule,
      scheduleForMath,
      levelPayment,
      parAmount: roundedPar,
      grossProceeds,
      netProceeds,
      coi,
      capiUse,
    };
  }

  if (targetType === "par_amount") {
    const requestedPar = Number(targetValue);
    const res = solveForParAmount(requestedPar);
    if (shouldBypassDenominationOptimizer({ financingType })) {
      return {
        ...res,
        denominationOptimizationSkipped: true,
      };
    }

    const optimized = runDenominationOptimizer({
      result: res,
      optimizerTargetType: "par_amount",
    });

    return optimized;
  }

  if (targetType === "net_proceeds") {
    const denom = normalizeDenom(denomination);
    const bypassDenomOptimizer = shouldBypassDenominationOptimizer({ financingType });

    const rawTarget = Number(targetValue) || 0;
    const asAmt = Number(additionalSources) || 0;
    const proceedsTarget = Math.max(0, rawTarget - asAmt);

    const tightenLoanPrincipal = (current) => {
      if (!bypassDenomOptimizer || !current) return current;

      const lowerPar = roundCents(Number(current.parAmount || 0) - 0.01);
      if (!(lowerPar > 0)) return current;

      const candidate = solveForParAmount(lowerPar);
      return Number(candidate.netProceeds || 0) + 0.005 >= proceedsTarget
        ? candidate
        : current;
    };

    let par = Math.max(denom, Math.round(proceedsTarget / denom) * denom);
    let result = null;

    for (let i = 0; i < 100; i++) {
      result = solveForParAmount(par);

      const coiNow =
        typeof costOfIssuance === "function"
          ? costOfIssuance(result.parAmount)
          : Number(costOfIssuance) || 0;

      const capiNow = Number(result.capiUse || 0);
      const netNow = roundCents(result.grossProceeds - coiNow - capiNow);
      const diff = netNow - proceedsTarget;

      if (diff >= 0 && diff < denom) {
        if (bypassDenomOptimizer) {
          return {
            ...tightenLoanPrincipal(result),
            denominationOptimizationSkipped: true,
          };
        }

        return runDenominationOptimizer({
          result,
          optimizerTargetType: "net_proceeds",
          proceedsTarget,
        });
      }

      const scale = proceedsTarget / (netNow || 1);
      par = par * scale;

      par =
        diff < 0
          ? Math.ceil(par / denom) * denom
          : Math.max(denom, Math.round(par / denom) * denom);

      if (!Number.isFinite(par) || par <= 0) par = denom;
    }

    let guard = 0;
    let res = null;
    let coiNow = 0;
    let capiNow = 0;
    let netNow = 0;

    while (guard < 50) {
      res = solveForParAmount(par);

      coiNow =
        typeof costOfIssuance === "function"
          ? costOfIssuance(res.parAmount)
          : Number(costOfIssuance) || 0;

      capiNow = Number(res.capiUse || 0);
      netNow = roundCents(res.grossProceeds - coiNow - capiNow);

      if (netNow + 0.005 >= proceedsTarget) {
        console.warn(
          `[LDS Solver] ⚠️ Forced sufficiency at par=${par} (net=${netNow.toFixed(
            2
          )} target=${proceedsTarget.toFixed(2)})`
        );
        break;
      }

      par += denom;
      guard++;
    }

    if (bypassDenomOptimizer) {
      return {
        ...tightenLoanPrincipal(res),
        denominationOptimizationSkipped: true,
      };
    }

    return runDenominationOptimizer({
      result: res,
      optimizerTargetType: "net_proceeds",
      proceedsTarget,
    });
  }

  if (targetType === "periodic_ds") {
    const targetPayment = Number(targetValue) || 0;
    if (!(targetPayment > 0)) {
      throw new Error("targetValue must be > 0 for periodic_ds");
    }

    const denom = normalizeDenom(denomination);
    const bypassDenomOptimizer = shouldBypassDenominationOptimizer({ financingType });
    const dc = dayCount || "30/360";

    const expanded = buildExpandedRows();

    const couponAnnual = expanded.map((m) => {
      let x = Number(m?.coupon ?? 0);
      if (!Number.isFinite(x)) x = 0;
      if (x > 1) x = x / 100;
      if (x < 0) x = 0;
      return x;
    });

    const { segments } = buildSegments(expanded);
    const numPeriods = segments.length;

    const principalEndDates = segments
      .map((seg) => expanded?.[seg.end]?.date)
      .filter(Boolean);

    const segAccrualFactor = computeSegmentAccrualFactors({
      datedDate,
      firstCouponDate,
      finalMaturityDate,
      couponFrequency,
      dayCount: dc,
      principalEndDates,
      principalStepMonths: principalFreqToStepMonths(),
    });

    const segmentCouponAnnual = segments.map(
      (seg) => couponAnnual?.[seg.end] || 0
    );

    const segmentTargetFactors = computeSegmentTargetFactors({
      segments,
      expanded,
      dayCount: dc,
    });

    const segmentTargetAdjustments = computeSegmentTargetAdjustments({
      segments,
    });

    const segmentAccruedInterestFactors = computeSegmentAccruedInterestFactors({
      segments,
      couponAnnual,
      dayCount: dc,
    });

    const baseTotals = buildBaseDebtServiceBySegment({
      baseDebtServiceByPeriod,
      wrapBaseByPeriod,
      segments,
      expanded,
    });

    const principalsByPeriod = Array(numPeriods).fill(0);
    for (let y = numPeriods - 1; y >= 0; y--) {
      const accrualFactor = segAccrualFactor[y] || 0;
      const currentInterestFactor =
        accrualFactor * (segmentCouponAnnual[y] || 0);

      const targetY =
        targetPayment * (segmentTargetFactors[y] || 1) +
        Number(segmentTargetAdjustments[y] || 0) -
        num(baseTotals[y], 0);

      const accruedLater =
        y === 0
          ? principalsByPeriod
              .slice(y + 1)
              .reduce(
                (s, p, offset) =>
                  s + p * (segmentAccruedInterestFactors[y + 1 + offset] || 0),
                0
              )
          : 0;

      const accruedCurrentFactor =
        y === 0 ? segmentAccruedInterestFactors[y] || 0 : 0;

      const laterInterest = principalsByPeriod
        .slice(y + 1)
        .reduce(
          (sum, principal, offset) =>
            sum +
            principal *
              accrualFactor *
              (segmentCouponAnnual[y + 1 + offset] || 0),
          0
        );

      const capNumer = targetY + accruedLater - laterInterest;
      const denomTerm = 1 + currentInterestFactor - accruedCurrentFactor;

      let maxPy = denomTerm <= 0 || capNumer <= 0 ? 0 : capNumer / denomTerm;

      if (maxPy < 0) maxPy = 0;

      const Py = bypassDenomOptimizer
        ? roundCents(maxPy)
        : Math.max(0, Math.floor(maxPy / denom) * denom);

      principalsByPeriod[y] = Py;
    }

    let scheduleForMath = expanded.map((m) => ({ ...m, principal: 0 }));

    for (let y = 0; y < numPeriods; y++) {
      const idx = segments[y].end;
      scheduleForMath[idx].principal = principalsByPeriod[y];
    }

    const parAmount = scheduleForMath.reduce((s, r) => s + (r.principal || 0), 0);

    const grossProceeds = scheduleForMath.reduce((acc, r) => {
      const p3 = roundPrice3(r.price ?? 100);
      const row = roundCents((r.principal || 0) * (p3 / 100));
      return acc + row;
    }, 0);

    const coi =
      typeof costOfIssuance === "function"
        ? costOfIssuance(parAmount)
        : Number(costOfIssuance) || 0;

    const netProceeds = roundCents(grossProceeds - coi);

    const schedule = collapseTermGroupsForPresentation(scheduleForMath);

    for (let y = 0; y < numPeriods; y++) {
      const accrualFactor = segAccrualFactor[y] || 0;
      const interestY = roundCents(
        principalsByPeriod.slice(y).reduce(
          (sum, principal, offset) =>
            sum +
            principal *
              accrualFactor *
              (segmentCouponAnnual[y + offset] || 0),
          0
        )
      );

      const accruedY =
        y === 0
          ? roundCents(
              principalsByPeriod.reduce(
                (s, p, j) => s + p * (segmentAccruedInterestFactors[j] || 0),
                0
              )
            )
          : 0;

      const newIssueDsY = roundCents(interestY + principalsByPeriod[y] - accruedY);
      const totalDsY = roundCents(newIssueDsY + num(baseTotals[y], 0));

      const targetY =
        targetPayment * (segmentTargetFactors[y] || 1) +
        Number(segmentTargetAdjustments[y] || 0);

      if (totalDsY > targetY + 0.005) {
        console.warn(
          `[periodic_ds] total DS cap violation at period ${y}: ${totalDsY} > ${targetY}`
        );
      }
    }

    return {
      schedule,
      scheduleForMath,
      levelPayment: targetPayment,
      parAmount,
      grossProceeds,
      netProceeds,
      coi,
      capiUse: 0,
      denominationOptimizationSkipped: bypassDenomOptimizer,
    };
  }

  throw new Error(`Unsupported targetType='${targetType}'`);
}
